import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Loader2, ArrowLeft, Filter } from 'lucide-react'
import { ReportFilters, getDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { formatCurrency } from '@/lib/utils'
import {
  useMarketingFunnel,
  useMarketingRevenueFunnel,
  useMarketingSourceStage,
  useMarketingMqlSqlAcceptance,
} from '@/hooks/use-api'

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v.toFixed(1)}%`
const days = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v}`)
const num = (v: number | null | undefined) => (v ?? 0).toLocaleString()

function Loading() {
  return (
    <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  )
}

export default function MarketingFunnelReports() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReportFilterValues>(() => getDateRange('fiscal_year'))
  const params = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    owner_id: filters.owner_id,
  }

  const funnel = useMarketingFunnel(params)
  const revenue = useMarketingRevenueFunnel(params)
  const source = useMarketingSourceStage(params)
  const acceptance = useMarketingMqlSqlAcceptance(params)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/reports')}
          aria-label="Back to reports"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Reports
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Marketing Funnel</h1>
          <p className="text-sm text-muted-foreground">
            Contact lifecycle from first entry through to closed revenue. Nurture, On-Hold and Dead
            contacts are excluded.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportFilters value={filters} onChange={setFilters} defaultPeriod="fiscal_year" />
        </CardContent>
      </Card>

      {/* Report 1: Marketing Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Marketing Funnel</CardTitle>
          <CardDescription>
            Volume and progression through the marketing-owned stages on the Lead record (ends at
            MQL, the sales handover). Date range applies to lead created date. Avg days in stage
            reflects transitions recorded from launch onward.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnel.isLoading ? (
            <Loading />
          ) : (
            <Table aria-label="Marketing funnel by stage">
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Contact Count</TableHead>
                  <TableHead className="text-right">Conversion to Next</TableHead>
                  <TableHead className="text-right">Avg. Days in Stage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {funnel.data?.rows.map((r) => (
                  <TableRow key={r.stage}>
                    <TableCell className="font-medium">{r.stage}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(r.count)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pct(r.conversionToNext)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {days(r.avgDaysInStage)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Report 4: MQL → SQL Acceptance */}
      <Card>
        <CardHeader>
          <CardTitle>MQL → SQL Acceptance</CardTitle>
          <CardDescription>
            BDR performance at the MQL handover — of the leads that reached MQL, how many were
            accepted (converted to SQL) versus rejected. Date range applies to lead created date. (A
            rejected MQL reverted to Lead isn't distinguishable in current state.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {acceptance.isLoading ? (
            <Loading />
          ) : acceptance.data ? (
            <>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {[
                  ['MQL Volume', num(acceptance.data.mqlVolume)],
                  ['Converted to SQL', num(acceptance.data.convertedToSql)],
                  ['Pending', num(acceptance.data.pending)],
                  ['Rejected', num(acceptance.data.rejected)],
                  ['Acceptance Rate', pct(acceptance.data.acceptanceRate)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border p-3">
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Rejection breakdown</p>
                <p className="mt-1 text-sm">
                  Nurture:{' '}
                  <span className="font-medium tabular-nums">
                    {num(acceptance.data.rejectionBreakdown.Nurture)}
                  </span>
                  {'   ·   '}
                  Dead:{' '}
                  <span className="font-medium tabular-nums">
                    {num(acceptance.data.rejectionBreakdown.Dead)}
                  </span>
                  {'   ·   '}
                  Reverted to Lead:{' '}
                  <span className="font-medium tabular-nums">
                    {num(acceptance.data.rejectionBreakdown.Reverted)}
                  </span>
                </p>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Report 2: Revenue Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Funnel</CardTitle>
          <CardDescription>
            MQL through to closed deals for marketing-generated opportunities. Date range applies to
            opportunity created date. Values in CAD.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {revenue.isLoading ? (
            <Loading />
          ) : revenue.data ? (
            <>
              <div className="mb-4 text-sm">
                <span className="text-muted-foreground">Win rate: </span>
                <span className="font-semibold tabular-nums">{pct(revenue.data.winRate)}</span>
              </div>
              <Table aria-label="Revenue funnel by stage">
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Conversion to Next</TableHead>
                    <TableHead className="text-right">Avg. Days in Stage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">MQL</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {num(revenue.data.volume.mql)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pct(revenue.data.conversion.mqlToSql)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {days(revenue.data.avgDays.mql)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">SQL / Opportunity</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {num(revenue.data.volume.sql)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(revenue.data.value.openPipeline, 'CAD')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pct(revenue.data.conversion.sqlToClosed)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {days(revenue.data.avgDays.sql)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Closed Won</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {num(revenue.data.volume.won)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(revenue.data.value.won, 'CAD')}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Closed Lost</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {num(revenue.data.volume.lost)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                    <TableCell className="text-right text-muted-foreground">N/A</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Report 3: Lead Source vs Marketing Stage */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Source vs Marketing Stage</CardTitle>
          <CardDescription>
            Lead distribution across marketing stages by lead source. Non-marketing sources are
            excluded. Date range applies to lead created date.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {source.isLoading ? (
            <Loading />
          ) : source.data ? (
            <Table aria-label="Lead source by marketing stage">
              <TableHeader>
                <TableRow>
                  <TableHead>Lead Source</TableHead>
                  {source.data.stages.map((s) => (
                    <TableHead key={s} className="text-right">
                      {s}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {source.data.rows.map((row) => (
                  <TableRow key={row.source}>
                    <TableCell className="font-medium">{row.source}</TableCell>
                    {source.data!.stages.map((s) => (
                      <TableCell key={s} className="text-right tabular-nums">
                        {num(row[s] as number)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums font-medium">
                      {num(row.total)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>TOTAL</TableCell>
                  {source.data.stages.map((s) => (
                    <TableCell key={s} className="text-right tabular-nums">
                      {num(source.data!.totalRow[s] as number)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums">
                    {num(source.data.totalRow.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
